"use client";

import leaveClass from "@/actions/instructor-page/membership-management/leave-class";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/shadcn/alert-dialog";
import { Button } from "@/components/shadcn/button";
import { useMutation } from "@tanstack/react-query";
import { LogOut } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";

interface StudentLeaveClassButtonProps {
    classId: number;
    className: string;
    onClassLeft?: () => void;
}

export default function StudentLeaveClassButton({
    classId,
    className,
    onClassLeft,
}: StudentLeaveClassButtonProps) {
    const t = useTranslations("classes");
    const tRoot = useTranslations();
    const [showLeaveDialog, setShowLeaveDialog] = useState(false);

    const leaveClassMutation = useMutation({
        mutationFn: (classId: number) => leaveClass(classId),
        onSuccess: (result) => {
            if (result.success) {
                toast.success(t("leftClass"));
                onClassLeft?.();
            } else {
                toast.error(
                    "error" in result
                        ? result.error?.message || t("leaveClassError")
                        : t("leaveClassError"),
                );
            }
            setShowLeaveDialog(false);
        },
        onError: (error) => {
            console.error("Error leaving class:", error);
            toast.error(t("leaveClassError"));
            setShowLeaveDialog(false);
        },
    });

    const handleLeaveClass = () => {
        leaveClassMutation.mutate(classId);
    };

    return (
        <>
            <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLeaveDialog(true)}
                disabled={leaveClassMutation.isPending}
                className="text-red-600 hover:bg-red-50 hover:text-red-700"
            >
                <LogOut size={14} className="mr-1" />
                {t("leaveClass")}
            </Button>

            <AlertDialog
                open={showLeaveDialog}
                onOpenChange={setShowLeaveDialog}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {t("leaveClassTitle")}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="space-y-2">
                            <p>
                                <strong>{className}</strong>{" "}
                                {t("leaveClassBody")}
                            </p>
                            <p>{t("leaveClassNote")}</p>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            disabled={leaveClassMutation.isPending}
                        >
                            {tRoot("common.cancel")}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleLeaveClass}
                            disabled={leaveClassMutation.isPending}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {leaveClassMutation.isPending
                                ? t("leaving")
                                : t("leaveClass")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
